<script setup lang="ts">
import { ElMessage } from 'element-plus';
import { ref, onMounted, onUnmounted, PropType } from 'vue';
import { SvgIcon } from '@vue3/components';

interface IModule {
  id: string;
  name: string;
  icon: string;
  disabled?: boolean;
}

const emit = defineEmits(['click']);

const props = defineProps({
  modules: {
    type: Array as PropType<IModule[]>,
    default: () => [],
  },
});

const modulesRef = ref<HTMLElement>();
const guideRef = ref<HTMLElement>();
const showModules = ref(false);
const isDragging = ref(false);
const hasMoved = ref(false);

const modulesLeft = ref(0);

const position = ref({
  x: 0,
  y: 0,
});
const dragOffset = ref({
  x: 0,
  y: 0,
});
const startPosition = ref({
  x: 0,
  y: 0,
});

/**
 * 检查边界条件并调整模块位置
 *
 * 根据guideRef元素的边界和props中的modules数组长度计算模块应该放置的位置。
 * 如果guideRef元素的中点超过模块宽度加10像素，则将模块放置在左侧，
 * 否则将模块放置在右侧。
 */
const checkBound = () => {
  const { width, left } = guideRef.value.getBoundingClientRect();
  const modulesW = 88 * props.modules.length + 20;
  const leftExtraWidth = left + width / 2;

  if (leftExtraWidth > modulesW + 10) {
    modulesRef.value.classList.remove('right');
    modulesLeft.value = left + width / 2 - modulesW;
  } else {
    modulesRef.value.classList.add('right');
    modulesLeft.value = left + width / 2;
  }
};

const toggleModules = () => {
  if (hasMoved.value) return;

  if (!showModules.value) {
    checkBound();
    showModules.value = true;

    setTimeout(() => {
      modulesRef.value.classList.add('opacity-1');
    }, 0);
    return;
  }

  modulesRef.value.classList.remove('opacity-1');
  setTimeout(() => {
    showModules.value = false;
  }, 300);
};

const startDrag = (e) => {
  const { clientX, clientY } = e;

  startPosition.value = {
    x: clientX,
    y: clientY,
  };
  hasMoved.value = false;

  dragOffset.value = {
    x: clientX - position.value.x,
    y: clientY - position.value.y,
  };

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
};

const onDrag = (e) => {
  const { clientX, clientY } = e;

  const moveDistance = Math.sqrt(
    Math.pow(clientX - startPosition.value.x, 2) +
    Math.pow(clientY - startPosition.value.y, 2),
  );

  if (moveDistance > 5) {
    hasMoved.value = true;
    isDragging.value = true;
    e.preventDefault();

    checkBound();
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
  }

  if (!isDragging.value || !hasMoved.value) return;

  const newX = clientX - dragOffset.value.x;
  const newY = clientY - dragOffset.value.y;

  const maxX = window.innerWidth - 60;
  const maxY = window.innerHeight - 60;

  position.value = {
    x: Math.max(0, Math.min(newX, maxX)),
    y: Math.max(0, Math.min(newY, maxY)),
  };
};

const stopDrag = () => {
  isDragging.value = false;
  document.body.style.userSelect = '';
  document.body.style.webkitUserSelect = '';

  setTimeout(() => {
    hasMoved.value = false;
  }, 100);

  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};

onMounted(() => {
  position.value = {
    x: window.innerWidth - 120,
    y: window.innerHeight - 120,
  };
});

onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
});

const handleClick = (module: IModule) => {
  if (module.disabled) return ElMessage.info('暂未开放');

  toggleModules();
  emit('click', module.id);
};
</script>

<template>
  <div
    ref="guideRef"
    class="robot-guide-icon"
    :class="{ 'dragging': isDragging, 'opened': showModules }"
    :style="{
      left: position.x + 'px',
      top: position.y + 'px',
    }"
    @mousedown="startDrag"
    @click="toggleModules"
  />

  <div
    ref="modulesRef"
    class="robot-modules"
    :style="{
      display: !showModules ? 'none' : 'flex',
      left: modulesLeft + 'px',
      top: position.y + 3 + 'px',
    }"
  >
    <div
      v-for="module in props.modules"
      :key="module.id"
      class="robot-module"
      @click="handleClick(module)"
    >
      <svg-icon :icon="module.icon" />
      <span>{{ module.name }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.robot-guide {
  &-icon {
    cursor: pointer;
    position: fixed;
    z-index: 502;
    width: 66px;
    height: 76px;
    transition: background-image .15s ease-in-out, background-position .15s ease-in-out;
    background-repeat: no-repeat;
    background-size: 100% auto;
    background-position: 0 0;
    background-image: url('@app/assets/images/robot-entry/robot-opened-icon.png');

    &.opened {
      background-image: url('@app/assets/images/robot-entry/robot-icon.png');
    }
  }
}

.robot-modules {
  position: fixed;
  background: linear-gradient(90deg, #4189f4 0%, #3b85f5 60%,  #273c8b 100%), #3875F9;
  border-radius: 8px;
  display: flex;
  color: #fff;
  box-sizing: border-box;
  padding: 7px 32px 7px 0;
  z-index: 500;
  opacity: 0;
  transition: opacity .3s ease-in-out;

  &.opacity-1 {
    opacity: 1;
  }

  &.right {
    padding-left: 30px;
    padding-right: 12px;
  }
}

.robot-module {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  font-size: 12px;
  cursor: pointer;
  padding: 0 8px;
  margin: 0 12px;
  text-align: center;
  position: relative;
  border-radius: 8px;
  user-select: none;
  transition: background-color .15s ease-in-out;

  &:hover {
    background-color: rgba(255,255,255,0.1);
  }

  &::after {
    content: '';
    position: absolute;
    right: -12px;
    top: 7px;
    width: 1px;
    height: 36px;
    background: #2c58b5;
  }

  &:last-child {
    margin-right: 0;

    &::after {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.robot-module .svg-icon g {
  fill: #fff !important;
}
</style>
